import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { SubmissionTokenForm } from "@/javascript/conduction-api";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, NumberInput, Skeleton, Stack, Text } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext, useEffect, useState } from "react";
import ConductionModal from "../ConductionModal";

interface AdminClassroomSubmissionTokenCreatorProps {
	classroomId: string;
	onClose: () => void;
	onCreated: () => void;
}

const AdminClassroomSubmissionTokenCreator: React.FC<AdminClassroomSubmissionTokenCreatorProps> = ({ classroomId, onClose, onCreated }) => {
	const [classroom, setClassroom] = useState<Classroom | null>(null);
	const [loading, setLoading] = useState<boolean>(true);

	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminClassroomDeletionForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	const fetchClassroom = async () => {
		const notificationId = Utils.displayNotification(
			"Loading Classroom Data",
			undefined,
			"blue",
			"loading"
		);

		ConductionAPI.getClassroom(classroomId, token)
			.then((loadedClassroom: Classroom | null) => {
				if (loadedClassroom !== null) {
					setClassroom(loadedClassroom);
					Utils.updateNotification(
						notificationId,
						"Classroom Data Loaded",
						undefined,
						"green",
						<IconCheck size={16} />
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Error Fetching Classroom",
						"An error occurred while retrieving the classroom.",
						"red",
						<IconAlertCircle size={16} />,
						true
					);
				}
			})
			.catch(() => {
				Utils.updateNotification(
					notificationId,
					"Error Fetching Classroom",
					"An unexpected error occurred.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		fetchClassroom();
	}, []);

	const form = useForm({
		initialValues: {
			credits: 0,
			start: new Date(),
			expiration: new Date()
		},
		validate: {
			credits: (value) =>
				value <= 0
					? "Credits must be greater than zero."
					: null,
			start: (_) => null,
			expiration: (value, values) => {
				// Create copies of the dates to normalize their times.
				const normalizedStart = new Date(values.start);
				normalizedStart.setHours(0, 0, 0, 0); // 12:00 AM

				const normalizedExpiration = new Date(value);
				normalizedExpiration.setHours(23, 59, 59, 999); // 11:59 PM

				// Now, if the expiration is before the start day, return an error.
				return normalizedExpiration < normalizedStart
					? "Expiration date must be the same day or later than the start date."
					: null;
			}
		},
	});

	const handleSubmit = async () => {
		// Get the selected local dates
		const localStart = new Date(form.values.start);
		const localExpiration = new Date(form.values.expiration);

		// Normalize times: start at midnight and expiration at end-of-day
		localStart.setHours(0, 0, 0, 0);
		localExpiration.setHours(23, 59, 59, 999);

		// Convert to UTC using Date.UTC
		const startUTC = new Date(Date.UTC(
			localStart.getFullYear(),
			localStart.getMonth(),
			localStart.getDate()
		));

		const expirationUTC = new Date(Date.UTC(
			localExpiration.getFullYear(),
			localExpiration.getMonth(),
			localExpiration.getDate(),
			localExpiration.getHours(),
			localExpiration.getMinutes(),
			localExpiration.getSeconds(),
			localExpiration.getMilliseconds()
		));

		form.values.start = startUTC;
		form.values.expiration = expirationUTC;

		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Creating credits...",
			undefined,
			"blue",
			"loading"
		);

		const submissionTokenForm: SubmissionTokenForm = {
			credits: form.values.credits,
			start: form.values.start,
			expiration: form.values.expiration
		};

		try {
			await ConductionAPI.addSubmissionToken(classroomId, submissionTokenForm, token);

			Utils.updateNotification(
				notificationId,
				"Credits added!",
				undefined,
				"green",
				<IconCheck size={16} />
			);

			onCreated();
			onClose();
		} catch (error) {
			console.error("Error creating tokens", error);
			Utils.updateNotification(
				notificationId,
				"Error Occurred",
				undefined,
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title="Create Classroom Submission Token"
			size="md"
		>
			<Box style={{ minHeight: `${height}vh` }}>
				{loading ? (
					<Skeleton height="100%" radius="md" />
				) : classroom === null ? (
					<Text>Classroom data did not load correctly.</Text>
				) : (
					<form onSubmit={form.onSubmit(handleSubmit)}>
						<Flex direction="column" justify="space-between" align="stretch" style={{ minHeight: `${height}vh` }}>
							<Stack>
								<Text>
									Adding submission credits to classroom: {classroom.name}
								</Text>
								<NumberInput
									label="Credits"
									required
									{...form.getInputProps("credits")}
								/>
								<Flex
									direction={"row"}
									justify={"space-between"}
								>
									<Stack
									>
										<Text>Start Date</Text>
										<DatePicker
											{...form.getInputProps("start")}
										/>
									</Stack>
									<Stack>
										<Text>Expiration Date</Text>
										<DatePicker
											{...form.getInputProps("expiration")}
										/>
									</Stack>
								</Flex>
								<Text c="red">{form.errors.expiration}</Text>
							</Stack>
							<Button type="submit" radius="xl" size="sm" loading={form.submitting} color="red">
								Create and Add Credits
							</Button>
						</Flex>
					</form>
				)}
			</Box>
		</ConductionModal>
	);
};

export default AdminClassroomSubmissionTokenCreator;
